import { InfoPageBackLink } from '@/components/layout/InfoPageBackLink';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-surface">
      <div className="ornamental-rule max-w-7xl mx-auto px-4 pt-6 sm:pt-8" />
      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
        <InfoPageBackLink />

        <div className="text-center mb-8">
          <span className="text-gold text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
            Legal
          </span>
          <h1 className="text-3xl sm:text-4xl font-serif font-bold text-cream mb-4">
            Terms of Service
          </h1>
          <p className="text-sm text-tertiary">Last updated: February 24, 2026</p>
        </div>

        <div className="space-y-8">
          <Section title="1. Acceptance of Terms">
            <p>By accessing or using FantasyPRIME (&quot;the Service&quot;), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.</p>
          </Section>

          <Section title="2. Eligibility">
            <p>You must be at least 18 years old to create an account and use FantasyPRIME. By using the Service, you represent and warrant that you meet this age requirement and that you have the legal capacity to enter into these terms.</p>
          </Section>

          <Section title="3. Accounts">
            <p>You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately of any unauthorized use of your account. We reserve the right to suspend or terminate accounts that violate these terms.</p>
          </Section>

          <Section title="4. Virtual Currency">
            <p>FantasyPRIME uses virtual currencies (&quot;Coins&quot; and &quot;Gems&quot;) within the platform. Virtual currency:</p>
            <ul>
              <li>Has no real-world monetary value outside of the platform</li>
              <li>Cannot be redeemed for cash or transferred to other users</li>
              <li>May be purchased with real money through our payment processor</li>
              <li>Is non-refundable once purchased, except where required by law</li>
              <li>May be modified, adjusted, or reset at our sole discretion</li>
            </ul>
          </Section>

          <Section title="5. Events and Competitions">
            <p>FantasyPRIME offers pick-em events and competitions. Event rules, scoring, and prize structures are set by FantasyPRIME and may be modified at any time. We reserve the right to void, cancel, or modify any event due to technical issues, rule violations, or other circumstances beyond our control.</p>
          </Section>

          <Section title="6. Prohibited Conduct">
            <p>You agree not to:</p>
            <ul>
              <li>Create multiple accounts or use another person&apos;s account</li>
              <li>Use automated systems, bots, or scripts to interact with the Service</li>
              <li>Exploit bugs, glitches, or vulnerabilities for unfair advantage</li>
              <li>Engage in collusion or any form of cheating</li>
              <li>Harass, threaten, or abuse other users</li>
              <li>Attempt to reverse-engineer or decompile the Service</li>
            </ul>
          </Section>

          <Section title="7. Intellectual Property">
            <p>All content, graphics, design, and software on FantasyPRIME are the property of FantasyPRIME or its licensors. You may not copy, modify, distribute, or create derivative works from any part of the Service without our express written permission.</p>
          </Section>

          <Section title="8. Disclaimer of Warranties">
            <p>The Service is provided &quot;as is&quot; and &quot;as available&quot; without warranties of any kind, whether express or implied. We do not guarantee that the Service will be uninterrupted, error-free, or free of harmful components.</p>
          </Section>

          <Section title="9. Limitation of Liability">
            <p>To the maximum extent permitted by law, FantasyPRIME and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service. Our total liability shall not exceed the amount you have paid to FantasyPRIME in the 12 months preceding the claim.</p>
          </Section>

          <Section title="10. Modifications">
            <p>We reserve the right to modify these Terms of Service at any time. Material changes will be posted on this page with an updated &quot;Last updated&quot; date. Your continued use of the Service after changes are posted constitutes acceptance of the revised terms.</p>
          </Section>

          <Section title="11. Governing Law">
            <p>These terms shall be governed by and construed in accordance with the laws of the United States. Any disputes arising under these terms shall be resolved through binding arbitration, except where prohibited by law.</p>
          </Section>

          <Section title="12. Contact">
            <p>If you have questions about these terms, please contact us at <span className="text-gold">support@fantasyprime.com</span>.</p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-serif font-bold text-cream mb-3">{title}</h2>
      <div className="text-warm-gray text-sm space-y-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_li]:text-warm-gray">
        {children}
      </div>
    </section>
  );
}
